
export enum UserRole {
  ADMIN = 'admin',
  USER = 'user'
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

export enum MatchStatus {
  EXACT = '完全一致',
  WITH_FEE = '手数料差引',
  MANUAL_MATCH = '手動照合',
  NEEDS_REVIEW = '要確認',
  OTHER = 'その他',
  UNMATCHED = '未照合'
}

export interface Client {
  id: string;
  client_code: string;
  client_name: string;
  client_kana: string;
  client_abbreviation?: string;
  imported_name?: string;
  densai_user_code?: string; // でんさい利用者番号
  is_union: boolean;
  union_type?: 'satsuma' | 'minamisatsuma';
  notes?: string;
  balance?: number;
  created_at?: string;
  updated_at?: string;
}

export interface BillingRecord {
  id: string;
  client_code: string;
  client_name: string;
  client_kana: string;
  import_month: string; // YYYY/MM
  billing_balance: number;
  import_date: string;
  notes?: string;
  moved_from_month?: string; // 翌月移動前の元の請求月（前月へ戻す用）
  already_matched?: boolean; // 手動照合モーダル用：別入金にすでに照合済みかどうか
}

export interface BankDeposit {
  id: string;
  deposit_date: string;
  remitter_name: string;
  amount: number;
  memo?: string;
  client_code?: string;
  is_union_customer?: boolean;
  is_other?: boolean;
  is_densai?: boolean; // でんさい由来かどうか
  import_date: string;
  notes?: string;
  display_month?: string; // 翌月移動後の表示月上書き（前月へ戻す用）
}

export interface Match {
  id: string;
  billing_id?: string;
  deposit_id: string;
  created_at: string;
  match_type: MatchStatus;
  manually_approved: boolean;
  notes?: string;
  client_code?: string;
  client_name?: string;
  allocated_deposit_amount?: number; // 手動照合（一括入金）時の入金配分額
}

export interface ReconciliationRecord {
  id: string;
  type: 'matched' | 'billing' | 'deposit'; 
  client_code: string;
  client_name: string;
  remitter_name: string;
  billing_month: string;
  billing_amount: number;
  deposit_amount: number;
  difference: number;
  deposit_date: string;
  status: MatchStatus;
  notes: string;
  is_union: boolean;
  is_other: boolean;
  is_monthly: boolean;
  is_densai: boolean; // Added
  manually_approved: boolean;
  billing_id?: string;
  deposit_id?: string;
  // UI helper props
  candidate_codes?: string[];
  multi_billing_client?: boolean;
  moved_from_month?: string; // 翌月移動された請求データの元の月
  display_month?: string;    // 翌月移動された入金データの表示月
  billing_count?: number;    // 一括入金照合時の請求件数
  billing_amounts?: number[]; // 一括入金照合時の個別請求金額リスト
}

export interface UnionStatementBatch {
  id: string;
  union_type: 'satsuma' | 'minamisatsuma';
  year_month: string;
  created_at: string;
  total_count: number;
  total_billing: number;
  total_deposit: number;
  status: 'pending' | 'approved';
  deposit_date?: string; // 組合からの入金日（手入力で保存可能、YYYY/MM/DD形式）
}

export interface UnionStatementRecord {
  id: string;
  batch_id: string;
  client_code: string;
  client_name: string;
  client_abbreviation?: string;
  category?: string; // 区分
  shipping?: string; // 出荷
  billing_amount: number;
  deposit_amount: number;
  difference: number;
  status: 'matched' | 'unmatched' | 'approved';
  notes?: string;
  sort_order?: number; // 元PDFの並び順を保持するためのインデックス
}

export interface ReportTemplate {
  id: string;
  name: string;
  title: string;
  visibleColumns: string[];
}

export interface AutoOtherRule {
  id: string;
  remitter_name: string;
  amount: number;
  client_code: string;
  client_name?: string;
  notes: string;
}

export interface RecurringPayment {
  id: string;
  client_code: string;
  client_name?: string;
  remitter_name: string;
  expected_amount: number;
  notes: string;
  is_alias_only?: boolean; // 統計には含めず、照合ルールとしてのみ使用する場合true
}

// でんさい予約（発生記録）
export interface DensaiReservation {
  id: string;
  client_code: string;       // マスタから特定した得意先CD
  client_name: string;       // マスタ名称
  densai_user_code: string;  // PDF記載の利用者番号
  remitter_name_pdf: string; // PDF記載の法人名/個人事業者名
  payment_date: string;      // 支払期日 (YYYY/MM/DD)
  amount: number;            // 債権金額
  record_number: string;     // 記録番号 (ユニークキーとして使用)
  status: 'pending' | 'completed'; // pending=入金待ち, completed=FB消込済
  linked_deposit_id?: string; // 消し込まれた銀行FBデータのID
  created_at: string;
}

// 組合支払サイクル設定（翌々月払い・翌々々月払い等）
export interface UnionPaymentTerm {
  id: string;
  client_code: string;
  client_name: string;
  payment_cycle: number; // 2=翌々月払い, 3=翌々々月払い
  payment_date: string;  // 支払日（例："5", "10", "25"）
  union_type: 'satsuma'; // 現時点では薩摩砕石のみ
}
